import React, { useEffect, useState, useRef } from 'react';
import { InputGroup, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import { BsSearch, BsClock, BsX } from 'react-icons/bs';
import { get } from '../../utils/api_helper';

const TEAL = '#2aa3ad';

const SearchBar = () => {
  const [listings, setListings] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [searchtext, setSearchText] = useState('');
  const [searchOption, setSearchOption] = useState({ option: 'products' });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchRef = useRef(null);
  const suggestionRefs = useRef([]);

  const options = [
    { value: 'products', label: 'Products' },
    { value: 'suppliers', label: 'Suppliers' },
  ];

  useEffect(() => {
    const getProducts = async () => {
      try { setListings(await get('/listings')); } catch (e) { console.error(e); }
    };
    const getSuppliers = async () => {
      try { setSuppliers(await get('/companies')); } catch (e) { console.error(e); }
    };
    getSuppliers();
    getProducts();

    // Load recent searches from localStorage (you can implement this)
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setActiveSuggestion(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onSelectionChange = (value, action) => {
    setSearchOption({ [action.name]: value.value });
    // reset suggestions on toggle
    if (searchtext) onChangeHandler(searchtext);
  };

  const onChangeHandler = (text) => {
    let matches = [];
    if (text?.length > 0) {
      if (searchOption.option === 'products') {
        matches = listings.filter((x) =>
          x?.title?.toLowerCase().includes(text.toLowerCase())
        );
      } else {
        matches = suppliers.filter((x) =>
          x?.name?.toLowerCase().includes(text.toLowerCase())
        );
      }
      setSuggestions(matches.slice(0, 8));
      setShowSuggestions(true);
    } else {
      // Show recent searches when input is empty but focused
      setSuggestions([]);
      setShowSuggestions(searchRef.current?.contains(document.activeElement));
    }
    setSearchText(text);
    setActiveSuggestion(-1);
  };

  const onSuggestHandler = (text) => {
    setSearchText(text);
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveSuggestion(-1);

    // Add to recent searches
    const updated = [text, ...recentSearches.filter(s => s !== text)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  const onFocusHandler = () => {
    if (searchtext.length === 0 && recentSearches.length > 0) {
      setShowSuggestions(true);
    } else if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    const totalItems = suggestions.length + (searchtext.length === 0 ? recentSearches.length : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestion(prev =>
          prev < totalItems - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestion(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (activeSuggestion >= 0) {
          const allItems = [
            ...(searchtext.length === 0 ? recentSearches : []),
            ...suggestions.map(s => s.title || s.name)
          ];
          onSuggestHandler(allItems[activeSuggestion]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setActiveSuggestion(-1);
        searchRef.current?.blur();
        break;
    }
  };

  const removeRecentSearch = (searchToRemove, e) => {
    e.stopPropagation();
    const updated = recentSearches.filter(s => s !== searchToRemove);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <strong key={i} style={{ color: TEAL }}>{part}</strong> : part
    );
  };

  // react-select styles: compact, left-rounded, separator hidden
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      height: 38,
      minHeight: 38,
      borderRadius: '12px 0 0 12px',
      borderColor: state.isFocused ? '#e5e7eb' : '#e5e7eb',
      boxShadow: 'none',
      ':hover': { borderColor: '#e5e7eb' },
      backgroundColor: '#fff',
      display: 'flex',
      alignItems: 'center',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px',
      height: 36,
      display: 'flex',
      alignItems: 'center',
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: 14,
      fontWeight: 600,
      margin: 0,
      transform: 'none',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      paddingRight: 8,
      height: 36,
      display: 'flex',
      alignItems: 'center',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    menu: (base) => ({ ...base, borderRadius: 8, overflow: 'hidden' }),
    option: (base, state) => ({
      ...base,
      fontSize: 14,
      padding: '8px 12px',
      background: state.isSelected ? TEAL : state.isFocused ? '#f2f6f7' : '#fff',
      color: state.isSelected ? '#fff' : '#111',
      cursor: 'pointer',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: 4,
    }),
  };

  const suggestionStyles = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderTop: 'none',
    borderRadius: '0 0 12px 12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    zIndex: 1000,
    maxHeight: '400px',
    overflowY: 'auto',
    animation: 'fadeIn 0.15s ease-out',
  };

  const suggestionItemStyles = (isActive, isRecent = false) => ({
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: isActive ? '#f8f9fa' : 'transparent',
    borderLeft: isActive ? `3px solid ${TEAL}` : '3px solid transparent',
    fontSize: '14px',
    color: '#374151',
    transition: 'all 0.15s ease',
    ':hover': {
      backgroundColor: '#f8f9fa',
    }
  });

  return (
    <>
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .sb-searchbar-wrap {
          position: relative;
        }
        
        .sb-suggestion-item:hover {
          background-color: #f8f9fa !important;
        }
        
        .sb-recent-header {
          padding: 8px 16px;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          background-color: #f9fafb;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .sb-no-results {
          padding: 20px 16px;
          text-align: center;
          color: #9ca3af;
          font-style: italic;
        }
      `}</style>

      <div className="sb-searchbar-wrap" ref={searchRef}>
        <form action="/search" method="GET">
          <InputGroup className="sb-inputgroup">
            <Select
              name="option"
              options={options}
              styles={selectStyles}
              defaultValue={options[0]}
              onChange={onSelectionChange}
              isSearchable={false}
              components={{ IndicatorSeparator: null }}
            />

            <Form.Control
              type="text"
              placeholder="Search here ..."
              name="s"
              value={searchtext}
              onChange={(e) => onChangeHandler(e.target.value)}
              onFocus={onFocusHandler}
              onKeyDown={handleKeyDown}
              autoComplete='off'
              style={{
                borderRadius: '0',
                borderLeft: 'none',
              }}
            />

            <Button type="submit" className="btn-info" style={{ borderRadius: '0 12px 12px 0' }}>
              <BsSearch size={16} />
            </Button>
          </InputGroup>
        </form>

        {showSuggestions && (
          <div style={suggestionStyles}>
            {/* Recent searches when input is empty */}
            {searchtext.length === 0 && recentSearches.length > 0 && (
              <>
                <div className="sb-recent-header">Recent Searches</div>
                {recentSearches.map((search, i) => (
                  <div
                    key={`recent-${i}`}
                    className="sb-suggestion-item"
                    style={suggestionItemStyles(activeSuggestion === i, true)}
                    onClick={() => onSuggestHandler(search)}
                  >
                    <BsClock size={14} color="#9ca3af" />
                    <span style={{ flex: 1 }}>{search}</span>
                    <BsX
                      size={16}
                      color="#9ca3af"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => removeRecentSearch(search, e)}
                    />
                  </div>
                ))}
              </>
            )}

            {/* Current search suggestions */}
            {suggestions.length > 0 && (
              <>
                {searchtext.length === 0 && recentSearches.length > 0 && (
                  <div style={{ height: '1px', backgroundColor: '#e5e7eb' }} />
                )}
                {suggestions.map((s, i) => {
                  const text = s.title || s.name;
                  const adjustedIndex = searchtext.length === 0 ? recentSearches.length + i : i;
                  return (
                    <div
                      key={i}
                      className="sb-suggestion-item"
                      style={suggestionItemStyles(activeSuggestion === adjustedIndex)}
                      onClick={() => onSuggestHandler(text)}
                    >
                      <BsSearch size={14} color="#9ca3af" />
                      <span style={{ flex: 1 }}>
                        {highlightMatch(text, searchtext)}
                      </span>
                    </div>
                  );
                })}
              </>
            )}

            {/* No results message */}
            {searchtext.length > 0 && suggestions.length === 0 && (
              <div className="sb-no-results">
                No results found for "{searchtext}"
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SearchBar;